import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { CurrentUser } from "../../../common/src";
import { onUnauthorized, trpc } from "../trpc";
import { getErrorMessage } from "../utils/errors";

type SessionState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "signedOut"; expired: boolean }
  | { status: "signedIn"; user: CurrentUser };

interface SessionContextValue {
  state: SessionState;
  signedIn: (user: CurrentUser) => void;
  signOut: () => Promise<void>;
  retry: () => void;
}

const SessionContext = createContext<SessionContextValue | undefined>(
  undefined
);

export const SessionProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<SessionState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    trpc.auth.me.query(undefined, { signal: controller.signal }).then(
      (user) => {
        if (!controller.signal.aborted) {
          setState(
            user
              ? { status: "signedIn", user }
              : { status: "signedOut", expired: false }
          );
        }
      },
      (error: unknown) => {
        if (!controller.signal.aborted) {
          setState({ status: "error", message: getErrorMessage(error) });
        }
      }
    );
    return () => controller.abort();
  }, [attempt]);

  useEffect(
    () =>
      onUnauthorized(() => {
        setState((prev) =>
          prev.status === "signedIn"
            ? { status: "signedOut", expired: true }
            : prev
        );
      }),
    []
  );

  const signedIn = useCallback(
    (user: CurrentUser) => setState({ status: "signedIn", user }),
    []
  );

  const signOut = useCallback(async () => {
    try {
      await trpc.auth.logout.mutate();
    } finally {
      setState({ status: "signedOut", expired: false });
    }
  }, []);

  const retry = useCallback(() => {
    setState({ status: "loading" });
    setAttempt((n) => n + 1);
  }, []);

  const value = useMemo(
    () => ({ state, signedIn, signOut, retry }),
    [state, signedIn, signOut, retry]
  );
  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};

export const useSession = (): SessionContextValue => {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error("useSession must be used inside SessionProvider");
  }
  return value;
};

/** The signed-in user; only valid beneath the signed-in part of the app. */
export const useCurrentUser = (): CurrentUser => {
  const { state } = useSession();
  if (state.status !== "signedIn") {
    throw new Error("useCurrentUser requires a signed-in session");
  }
  return state.user;
};
